import type {
  AuthorityEvidence,
  AuthorityGateResult,
  AuthorityRequirementKey,
  AuthorityRequirements,
  JurisdictionPack,
  JurisdictionPackId,
  ProceedingType,
} from "./types.js";

const reviewedStatusLabel = "Reviewed — Not Certified" as const;

const globalPrivateProceeding: AuthorityRequirements = {
  authorityAttestation: true,
  participantNotice: true,
  participantConsent: true,
  confidentialityCheck: true,
  courtApproval: false,
  warning:
    "Confirm recording authority, consent, confidentiality, and disclosure rules for this venue.",
};

const globalHearing: AuthorityRequirements = {
  ...globalPrivateProceeding,
  courtApproval: true,
  warning:
    "Do not record a hearing until express court or tribunal approval is documented.",
};

const globalAuthority = {
  "deposition-examination": globalPrivateProceeding,
  "investigation-interview": globalPrivateProceeding,
  conference: globalPrivateProceeding,
  hearing: globalHearing,
} satisfies Readonly<Record<ProceedingType, AuthorityRequirements>>;

const auPrivateProceeding: AuthorityRequirements = {
  authorityAttestation: true,
  participantNotice: true,
  participantConsent: true,
  confidentialityCheck: true,
  courtApproval: false,
  warning:
    "Confirm applicable surveillance-device, privacy, professional-duty, and confidentiality requirements.",
};

const auAuthority = {
  "deposition-examination": auPrivateProceeding,
  "investigation-interview": auPrivateProceeding,
  conference: auPrivateProceeding,
  hearing: {
    ...auPrivateProceeding,
    courtApproval: true,
    warning:
      "Express court or tribunal approval must be documented before hearing capture.",
  },
} satisfies Readonly<Record<ProceedingType, AuthorityRequirements>>;

const usDepositionAuthority: AuthorityRequirements = {
  authorityAttestation: true,
  participantNotice: true,
  participantConsent: false,
  confidentialityCheck: true,
  courtApproval: false,
  warning:
    "Confirm governing federal, state, local, protective-order, notice, and officer requirements.",
};

export const JURISDICTION_PACKS = {
  "GLOBAL-BASE": {
    id: "GLOBAL-BASE",
    name: "Global base",
    jurisdiction: "Unsupported or not yet validated jurisdiction",
    version: "2026.07",
    supported: false,
    reviewedStatusLabel,
    authorityByProceeding: globalAuthority,
    layout: {
      paper: "A4",
      fontFamily: "Times New Roman",
      fontSizePt: 10,
      lineSpacing: "single",
      maxCharactersPerLine: 78,
      linesPerPage: 45,
      lineNumberInterval: 5,
      includeEventTimestamps: true,
    },
    certificationNotice:
      "AI-assisted transcript. Reviewed — Not Certified. No claim of court approval or official reporter certification.",
    privacyNotice:
      "Apply venue-specific privacy, residency, disclosure, and retention requirements.",
    retentionDays: 365,
    legalReviewDate: "2026-07-30T00:00:00.000Z",
  },
  "AU-NSW": {
    id: "AU-NSW",
    name: "Australia — New South Wales",
    jurisdiction: "New South Wales, Australia",
    version: "2026.07",
    supported: true,
    reviewedStatusLabel,
    authorityByProceeding: auAuthority,
    layout: {
      paper: "A4",
      fontFamily: "Times New Roman",
      fontSizePt: 10,
      lineSpacing: "single",
      maxCharactersPerLine: 78,
      linesPerPage: 45,
      lineNumberInterval: 5,
      includeEventTimestamps: true,
    },
    certificationNotice:
      "Dicta Verified Export · Reviewed — Not Certified. Not an official court transcript.",
    privacyNotice:
      "Confirm Australian Privacy Act, NSW surveillance-device law, confidentiality, court-direction, and data-residency obligations.",
    retentionDays: 365,
    legalReviewDate: "2026-07-30T00:00:00.000Z",
  },
  "US-FED-DEPOSITION": {
    id: "US-FED-DEPOSITION",
    name: "United States — Federal deposition",
    jurisdiction: "United States federal civil deposition",
    version: "2026.07",
    supported: true,
    reviewedStatusLabel,
    authorityByProceeding: {
      "deposition-examination": usDepositionAuthority,
      "investigation-interview": {
        ...globalPrivateProceeding,
        warning:
          "This pack validates deposition layout only. Confirm recording and consent law for the interview location.",
      },
      conference: {
        ...globalPrivateProceeding,
        warning:
          "This pack validates deposition layout only. Confirm recording and consent law for every participant location.",
      },
      hearing: {
        ...globalHearing,
        warning:
          "This pack does not validate hearing capture. Express court approval and applicable local rules are required.",
      },
    },
    layout: {
      paper: "Letter",
      fontFamily: "Courier Prime",
      fontSizePt: 12,
      lineSpacing: "double",
      maxCharactersPerLine: 63,
      linesPerPage: 25,
      lineNumberInterval: 1,
      includeEventTimestamps: false,
    },
    certificationNotice:
      "Dicta Verified Export · Reviewed — Not Certified. Officer certification and errata placeholders are not certifications.",
    privacyNotice:
      "Confirm federal, state, local, protective-order, disclosure, residency, and retention obligations.",
    retentionDays: 365,
    legalReviewDate: "2026-07-30T00:00:00.000Z",
  },
} as const satisfies Readonly<Record<JurisdictionPackId, JurisdictionPack>>;

export function getJurisdictionPack(
  id: JurisdictionPackId,
): JurisdictionPack {
  return JURISDICTION_PACKS[id];
}

export function resolveJurisdictionPack(id: string | null | undefined): {
  readonly pack: JurisdictionPack;
  readonly usedFallback: boolean;
  readonly warning: string | null;
} {
  if (id && Object.prototype.hasOwnProperty.call(JURISDICTION_PACKS, id)) {
    return {
      pack: JURISDICTION_PACKS[id as JurisdictionPackId],
      usedFallback: false,
      warning: null,
    };
  }

  return {
    pack: JURISDICTION_PACKS["GLOBAL-BASE"],
    usedFallback: true,
    warning:
      "Jurisdiction not validated. GLOBAL-BASE selected; lawyer authority attestation required.",
  };
}

const requirementMap: readonly {
  readonly requirement: keyof AuthorityRequirements;
  readonly evidence: AuthorityRequirementKey;
}[] = [
  { requirement: "authorityAttestation", evidence: "authorityAttested" },
  { requirement: "participantNotice", evidence: "participantNoticeGiven" },
  {
    requirement: "participantConsent",
    evidence: "participantConsentObtained",
  },
  {
    requirement: "confidentialityCheck",
    evidence: "confidentialityChecked",
  },
  { requirement: "courtApproval", evidence: "courtApprovalObtained" },
];

export function evaluateAuthorityGate(
  pack: JurisdictionPack,
  proceedingType: ProceedingType,
  evidence: AuthorityEvidence,
): AuthorityGateResult {
  const requirements = pack.authorityByProceeding[proceedingType];
  const missing = requirementMap
    .filter(
      ({ requirement, evidence: evidenceKey }) =>
        requirements[requirement] === true && evidence[evidenceKey] !== true,
    )
    .map(({ evidence: evidenceKey }) => evidenceKey);

  return {
    allowed: missing.length === 0,
    missing,
    warning: requirements.warning,
  };
}
